import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, Search, X } from 'lucide-react';
import type { Client } from '@oneup/shared';
import { clientsApi } from '@/lib/api';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface OrganizationPickerProps {
  value?: string;
  valueLabel?: string;
  placeholder?: string;
  onChange: (id: string, name: string) => void;
  onClear?: () => void;
}

export function OrganizationPicker({
  value,
  valueLabel,
  placeholder = '+ Add organization',
  onChange,
  onClear,
}: OrganizationPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['clients', 'all'],
    queryFn: () => clientsApi.list(),
    staleTime: 5 * 60_000,
  });

  const filtered = useMemo<Client[]>(() => {
    const items = data?.items ?? [];
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter((client) => client.name.toLowerCase().includes(q));
  }, [data?.items, search]);

  const displayValue = valueLabel || value;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="relative">
        <PopoverTrigger asChild>
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className={cn(
              'flex h-9 w-full items-center justify-between gap-1.5 rounded-md border border-input bg-background px-2.5 text-left text-[12px]',
              displayValue && onClear ? 'pr-7' : 'pr-2.5',
              'transition-colors hover:border-foreground/20',
            )}
          >
            <span
              className={cn('truncate', displayValue ? 'text-foreground' : 'text-muted-foreground')}
            >
              {displayValue ?? placeholder}
            </span>
            {!displayValue && (
              <ChevronDown className="h-3 w-3 flex-shrink-0 text-muted-foreground" aria-hidden />
            )}
          </button>
        </PopoverTrigger>
        {displayValue && onClear && (
          <button
            type="button"
            aria-label="Clear organization"
            onClick={(event) => {
              event.stopPropagation();
              onClear();
            }}
            className="absolute right-2 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="h-3 w-3" aria-hidden />
          </button>
        )}
      </div>
      <PopoverContent className="w-[260px] p-0" align="start">
        <div className="relative border-b border-border">
          <Search
            className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search organizations..."
            autoFocus
            className="h-8 w-full bg-transparent pl-7 pr-2 text-[12px] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          />
        </div>
        <ul className="max-h-[260px] overflow-y-auto py-1">
          {isLoading && <li className="px-3 py-2 text-[12px] text-muted-foreground">Loading...</li>}
          {!isLoading && filtered.length === 0 && (
            <li className="px-3 py-2 text-[12px] text-muted-foreground">No organizations match.</li>
          )}
          {filtered.slice(0, 80).map((client) => (
            <li key={client.id}>
              <button
                type="button"
                onClick={() => {
                  onChange(client.id, client.name);
                  setOpen(false);
                  setSearch('');
                }}
                className={cn(
                  'flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-[12px] transition-colors hover:bg-muted focus-visible:bg-muted',
                  value === client.id && 'bg-primary/10 text-primary',
                )}
              >
                <span className="truncate">{client.name}</span>
                {client.relationshipType && (
                  <span className="ml-2 flex-shrink-0 text-[10px] text-muted-foreground">
                    {client.relationshipType}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
